import { useCallback, useEffect, useState } from "react";
import { History, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ACTION_META = {
  "utilisateur.role": { label: "Changement de rôle", cls: "bg-bleu-light text-bleu" },
  "utilisateur.suspension": { label: "Suspension de compte", cls: "bg-rose-100 text-rose-600" },
  "utilisateur.reactivation": { label: "Réactivation de compte", cls: "bg-emerald-100 text-emerald-700" },
  "utilisateur.credit": { label: "Crédit Premium offert", cls: "bg-amber-100 text-amber-700" },
  "partenaire.validation": { label: "Partenaire validé", cls: "bg-emerald-100 text-emerald-700" },
  "partenaire.rejet": { label: "Partenaire rejeté", cls: "bg-rose-100 text-rose-600" },
  "contenu.creation": { label: "Contenu créé", cls: "bg-bleu-light text-bleu" },
  "contenu.modification": { label: "Contenu modifié", cls: "bg-amber-100 text-amber-700" },
  "contenu.suppression": { label: "Contenu supprimé", cls: "bg-rose-100 text-rose-600" },
  "question.creation": { label: "Question créée", cls: "bg-bleu-light text-bleu" },
  "question.modification": { label: "Question modifiée", cls: "bg-amber-100 text-amber-700" },
  "question.suppression": { label: "Question supprimée", cls: "bg-rose-100 text-rose-600" },
  "suggestion.modification": { label: "Suggestion mise à jour", cls: "bg-amber-100 text-amber-700" },
  "suggestion.reponse": { label: "Réponse à une suggestion", cls: "bg-emerald-100 text-emerald-700" },
};

const DETAIL_LABELS = {
  email: "Email", nouveau_role: "Nouveau rôle", organisation: "Organisation", motif: "Motif",
  titre: "Titre", section: "Section", publie: "Publié", champs: "Champs modifiés",
  jours: "Jours offerts", premium_jusqu_au: "Premium jusqu'au",
  question: "Question", theme: "Thématique", reponse: "Réponse", status: "Statut", priority: "Priorité",
};

function detailText(details) {
  return Object.entries(details || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${DETAIL_LABELS[k] || k} : ${Array.isArray(v) ? v.join(", ") : String(v)}`)
    .join(" · ");
}

export default function AdminAudit() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    api.get("/admin/audit", { params: filter ? { action: filter } : {} })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger le journal d'activité."));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const groups = [
    { value: "", label: `Tout (${data.total})` },
    ...data.actions.map((a) => ({ value: a, label: ACTION_META[a]?.label || a })),
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="page-admin-audit">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <History className="h-7 w-7 text-bleu" /> Journal d'activité
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Historique des actions d'administration : qui a suspendu, validé ou modifié quoi, et quand (200 dernières actions).
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="admin-audit-filters">
        {groups.map((g) => (
          <button key={g.value} onClick={() => setFilter(g.value)}
            className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold transition-colors ${
              filter === g.value ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"
            }`}
            data-testid={`admin-audit-filter-${g.value || "all"}`}>
            {g.label}
          </button>
        ))}
      </div>

      {data.entries.length === 0 ? (
        <div className="card-integra p-10 text-center" data-testid="admin-audit-empty">
          <History className="mx-auto h-10 w-10 text-slate-200" />
          <p className="mt-3 text-sm font-bold text-slate-700">Aucune action enregistrée {filter ? "pour ce type" : "pour le moment"}</p>
          <p className="mt-1 text-xs text-slate-400">Les actions d'administration (suspensions, validations, modifications) apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-audit-list">
          {data.entries.map((e) => {
            const meta = ACTION_META[e.action] || { label: e.action, cls: "bg-slate-100 text-slate-600" };
            return (
              <div key={e.id} className="card-integra flex flex-wrap items-start gap-2.5 p-4" data-testid="admin-audit-entry">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${meta.cls}`} data-testid="admin-audit-action-chip">{meta.label}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-slate-600">{detailText(e.details) || `Cible : ${e.target_type} ${e.target_id}`}</p>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Par <span className="font-semibold text-slate-500">{e.admin_name || e.admin_email}</span>
                    {" · "}{new Date(e.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
